import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { useGetUsersHandler } from '../dataHandlers/getUsersHandler';
import UserActions from './UserActions';

const paperSx = {
  bgcolor: 'background.paper',
  border: 1,
  borderColor: 'divider',
  borderRadius: 2,
  overflow: 'hidden',
};

const headerCellSx = {
  color: 'text.secondary',
  fontSize: '0.75rem',
  fontWeight: 500,
  borderBottom: 1,
  borderColor: 'divider',
  py: 2,
};

const bodyCellSx = {
  borderBottom: 1,
  borderColor: 'divider',
  py: 2,
};

const getStatusChip = (status) => {
  if (status === 'invited') {
    return <Chip label="Invited" size="small" variant="outlined" />;
  }
  if (status === 'active') {
    return <Chip label="Active" size="small" color="primary" />;
  }
  if (status === 'inactive') {
    return <Chip label="Inactive" size="small" color="warning" variant="outlined" />;
  }
  return <Chip label={status} size="small" />;
};

export default function UsersTable({ onEdit, searchQuery = {}, onPaginationChange }) {
  const { users, totalElements, isLoading } = useGetUsersHandler({ searchQuery });

  const handleChangePage = (_, newPage) => {
    onPaginationChange(newPage, searchQuery.size ?? 10);
  };

  const handleChangeRowsPerPage = (event) => {
    onPaginationChange(0, parseInt(event.target.value, 10));
  };

  if (isLoading && users.length === 0) {
    return (
      <Paper elevation={0} sx={paperSx}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={paperSx}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>User</TableCell>
              <TableCell sx={headerCellSx}>Email</TableCell>
              <TableCell sx={headerCellSx}>Status</TableCell>
              <TableCell align="right" sx={headerCellSx}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ ...bodyCellSx, borderBottom: 0 }}>
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No users yet. Invite someone to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow
                  key={user.id}
                  hover
                  sx={{
                    '&:last-child td': { borderBottom: 0 },
                  }}
                >
                  <TableCell sx={{ ...bodyCellSx, color: 'text.primary', fontWeight: 500 }}>{user.name}</TableCell>
                  <TableCell sx={{ ...bodyCellSx, color: 'text.secondary' }}>{user.email}</TableCell>
                  <TableCell sx={bodyCellSx}>{getStatusChip(user.status)}</TableCell>
                  <TableCell align="right" sx={bodyCellSx}>
                    <UserActions user={user} onEdit={onEdit} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalElements}
        page={searchQuery.page ?? 0}
        onPageChange={handleChangePage}
        rowsPerPage={searchQuery.size ?? 10}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
        sx={{ borderTop: 1, borderColor: 'divider' }}
      />
    </Paper>
  );
}
